import { InterventionCompletionStatus, ReviewStatus } from '@inplay/models';
import { Option } from '../../component/dropDownInput/dropDownInput.type';
import {
  FilterFieldConfig,
  FilterFieldType,
} from '../../component/filterPanel/filterPanel.types';
import { OptionGroup } from '../../component/multiSelectDropdown/multiSelectDropdown.type';
import {
  lengthOfPlayOptions,
  moneyOptions,
  behaviourOptions,
  otherOptions,
  ethinicityOptions,
  actionTakenOptions,
  followUpOptions,
  ageOptions,
} from '../../component/reportDetails/constants';
import { InterventionReportModel } from '../../models/interventionReportModel';

export const AUTO_GENERATED_CREATED_BY = '__AUTO_GENERATED__';

export const getGenderOptions = (t: (key: string) => string): Option[] => [
  { label: t('interventionReports.filters.gender.male'), value: 'Male' },
  { label: t('interventionReports.filters.gender.female'), value: 'Female' },
  { label: t('interventionReports.filters.gender.other'), value: 'Other' },
];

// Values pinned to the form's ageOptions (single source of truth) so the filter
// value always matches what the form persists on playerDetails.age.
export const getAgeOptions = (t: (key: string) => string): Option[] =>
  ageOptions.map((option: Option) => ({
    label: t(option.label),
    value: option.value,
  }));

export const getEthnicityOptions = (): Option[] => ethinicityOptions;

export const getInterventionStatusOptions = (
  t: (key: string) => string,
): Option[] => [
  {
    label: t('interventionReports.filters.interventionStatus.complete'),
    value: InterventionCompletionStatus.COMPLETE,
  },
  {
    label: t('interventionReports.filters.interventionStatus.missed'),
    value: InterventionCompletionStatus.MISSED,
  },
];

export const getReviewStatusOptions = (
  t: (key: string) => string,
): Option[] => [
  {
    label: t('interventionReports.filters.reviewStatus.pending'),
    value: ReviewStatus.PENDING,
  },
  {
    label: t('interventionReports.filters.reviewStatus.complete'),
    value: ReviewStatus.COMPLETE,
  },
  {
    label: t('interventionReports.filters.reviewStatus.missed'),
    value: ReviewStatus.MISSED,
  },
  {
    label: t('interventionReports.filters.reviewStatus.na'),
    value: ReviewStatus.NOT_APPLICABLE,
  },
];

// Compliance maps to the staffComplied boolean in the predicate layer.
export const getComplianceOptions = (t: (key: string) => string): Option[] => [
  {
    label: t('interventionReports.filters.compliance.compliant'),
    value: 'Compliant',
  },
  {
    label: t('interventionReports.filters.compliance.notCompliant'),
    value: 'Not-compliant',
  },
];

export const getHarmInstanceOptions = (
  t: (key: string) => string,
): Option[] => [
  { label: t('interventionReports.filters.harmInstance.1st'), value: '1' },
  { label: t('interventionReports.filters.harmInstance.2nd'), value: '2' },
  { label: t('interventionReports.filters.harmInstance.3rd'), value: '3' },
  { label: t('interventionReports.filters.harmInstance.4th'), value: '4' },
  { label: t('interventionReports.filters.harmInstance.5th'), value: '5' },
  { label: t('interventionReports.filters.harmInstance.6th'), value: '6' },
  { label: t('interventionReports.filters.harmInstance.7th'), value: '7' },
  { label: t('interventionReports.filters.harmInstance.8th'), value: '8' },
  { label: t('interventionReports.filters.harmInstance.9th'), value: '9' },
  { label: t('interventionReports.filters.harmInstance.10+'), value: '10+' },
];

export const getTotalInterventionsOptions = (
  t: (key: string) => string,
): Option[] => [
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5', value: '5' },
  { label: '6', value: '6' },
  { label: '7', value: '7' },
  { label: '8', value: '8' },
  {
    label: t('interventionReports.filters.totalInterventions.9+'),
    value: '9+',
  },
];

export const getHarmObservedGroups = (
  t: (key: string) => string,
): OptionGroup[] => [
  {
    groupLabel: t('interventionReports.filters.harmObserved.lengthOfPlay'),
    options: lengthOfPlayOptions,
  },
  {
    groupLabel: t('interventionReports.filters.harmObserved.money'),
    options: moneyOptions,
  },
  {
    groupLabel: t('interventionReports.filters.harmObserved.behaviour'),
    options: behaviourOptions,
  },
  {
    groupLabel: t('interventionReports.filters.harmObserved.other'),
    options: otherOptions,
  },
];

export const getActionTakenOptions = (t: (key: string) => string): Option[] =>
  actionTakenOptions.map((option: Option) => ({
    label: t(option.label),
    value: option.value,
  }));

export const getFollowUpActionOptions = (
  t: (key: string) => string,
): Option[] =>
  followUpOptions.map((option: Option) => ({
    label: t(option.label),
    value: option.value,
  }));

export const getPlayerNoOptions = (
  reports: InterventionReportModel[],
): Option[] => {
  const uniquePlayerIds: Set<string> = new Set();
  reports.forEach((report: InterventionReportModel) => {
    if (report.playerProfile?.id) {
      uniquePlayerIds.add(report.playerProfile.id);
    }
  });
  return Array.from(uniquePlayerIds).map((playerId: string) => ({
    label: playerId.slice(0, 5),
    value: playerId,
  }));
};

export const getConductedByOptions = (
  reports: InterventionReportModel[],
  t: (key: string) => string,
): Option[] => {
  const uniqueNames: Set<string> = new Set();
  let hasAutoGenerated: boolean = false;
  reports.forEach((report: InterventionReportModel) => {
    if (report.isAutoGenerated) {
      hasAutoGenerated = true;
    } else if (report.conductedBy?.firstName) {
      const fullName: string = `${report.conductedBy.firstName} ${report.conductedBy.lastName}`;
      uniqueNames.add(fullName);
    }
  });

  const manualOptions: Option[] = Array.from(uniqueNames).map(
    (name: string) => ({ label: name, value: name }),
  );

  if (hasAutoGenerated) {
    return [
      {
        label: t('reportDetails.autogeneratedReport'),
        value: AUTO_GENERATED_CREATED_BY,
      },
      ...manualOptions,
    ];
  }
  return manualOptions;
};

export const getReviewedByOptions = (
  reports: InterventionReportModel[],
): Option[] => {
  const uniqueNames: Set<string> = new Set();
  reports.forEach((report: InterventionReportModel) => {
    if (report.reviewedBy?.firstName) {
      const fullName: string = `${report.reviewedBy.firstName} ${report.reviewedBy.lastName}`;
      uniqueNames.add(fullName);
    }
  });
  return Array.from(uniqueNames).map((name: string) => ({
    label: name,
    value: name,
  }));
};

export const getInterventionReportFilterFields = (
  reports: InterventionReportModel[],
  t: (key: string) => string,
): FilterFieldConfig[] => [
  {
    key: 'sectionGeneral',
    label: t('interventionReports.filters.labels.sectionGeneral'),
    type: FilterFieldType.SECTION_HEADER,
  },
  {
    key: ['dateCreatedFrom', 'dateCreatedTo'],
    label: t('interventionReports.filters.labels.dateCreated'),
    type: FilterFieldType.DATE_RANGE,
  },
  {
    key: ['timeCreatedFrom', 'timeCreatedUntil'],
    label: t('interventionReports.filters.labels.timeCreated'),
    type: FilterFieldType.TIME_RANGE,
  },
  {
    key: 'playerNo',
    label: t('interventionReports.filters.labels.playerNo'),
    type: FilterFieldType.SELECT,
    options: getPlayerNoOptions(reports),
    search: true,
    placeholder: t('userManagement.select'),
  },
  {
    key: 'gender',
    label: t('interventionReports.filters.labels.gender'),
    type: FilterFieldType.SELECT,
    options: getGenderOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'age',
    label: t('interventionReports.filters.labels.age'),
    type: FilterFieldType.SELECT,
    options: getAgeOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'ethnicity',
    label: t('interventionReports.filters.labels.ethnicity'),
    type: FilterFieldType.SELECT,
    options: getEthnicityOptions(),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'interventionStatus',
    label: t('interventionReports.filters.labels.interventionStatus'),
    type: FilterFieldType.MULTI_SELECT,
    options: getInterventionStatusOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'harmInstances',
    label: t('interventionReports.filters.labels.harmInstances'),
    type: FilterFieldType.SELECT,
    options: getHarmInstanceOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'harmObserved',
    label: t('interventionReports.filters.labels.harmObserved'),
    type: FilterFieldType.GROUPED_MULTI_SELECT,
    groups: getHarmObservedGroups(t),
    search: true,
    placeholder: t('userManagement.select'),
  },
  {
    key: 'actionTaken',
    label: t('interventionReports.filters.labels.actionTaken'),
    type: FilterFieldType.MULTI_SELECT,
    options: getActionTakenOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'totalInterventionsOffered',
    label: t('interventionReports.filters.labels.totalInterventionsOffered'),
    type: FilterFieldType.SELECT,
    options: getTotalInterventionsOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'totalInterventionsRequested',
    label: t('interventionReports.filters.labels.totalInterventionsRequested'),
    type: FilterFieldType.SELECT,
    options: getTotalInterventionsOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'createdBy',
    label: t('interventionReports.filters.labels.createdBy'),
    type: FilterFieldType.SELECT,
    options: getConductedByOptions(reports, t),
    search: true,
    placeholder: t('userManagement.select'),
  },
  {
    key: 'sectionManagerReview',
    label: t('interventionReports.filters.labels.sectionManagerReview'),
    type: FilterFieldType.SECTION_HEADER,
    showDivider: true,
  },
  {
    key: 'reviewedBy',
    label: t('interventionReports.filters.labels.reviewedBy'),
    type: FilterFieldType.SELECT,
    options: getReviewedByOptions(reports),
    search: true,
    placeholder: t('userManagement.select'),
  },
  {
    key: ['dateReviewedFrom', 'dateReviewedTo'],
    label: t('interventionReports.filters.labels.dateReviewed'),
    type: FilterFieldType.DATE_RANGE,
  },
  {
    key: 'reviewStatus',
    label: t('interventionReports.filters.labels.reviewStatus'),
    type: FilterFieldType.MULTI_SELECT,
    options: getReviewStatusOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'compliance',
    label: t('interventionReports.filters.labels.compliance'),
    type: FilterFieldType.SELECT,
    options: getComplianceOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'followUpActions',
    label: t('interventionReports.filters.labels.followUpActions'),
    type: FilterFieldType.MULTI_SELECT,
    options: getFollowUpActionOptions(t),
    placeholder: t('userManagement.select'),
  },
];
